// Utilidades mejoradas para manejo de cámara con mejor soporte para iOS

import {SDK_EVENT_KEYS} from "./logService";
import {onReportEvent} from "../modules/cardCapture/utils";

export const initDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    return {
        isIos: /iphone|ipad|ipod/.test(userAgent),
        isAndroid: /android/.test(userAgent),
        isMac: /macintosh|mac os x/.test(userAgent),
        isSafari: /safari/.test(userAgent) && !/chrome/.test(userAgent),
        isWebView: window.navigator.standalone !== undefined || window.matchMedia("(display-mode: standalone)").matches,
    }
}

export const getVideoInputs = async () => {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        return devices.filter((device) => device.kind === "videoinput")
    } catch (error) {
        console.error("Error getting video inputs:", error)
        return []
    }
}
const fetchConfig = async (model) => {
    return new Promise(async (resolve) => {
        try {
            const response = await fetch(`https://lnesv3-conf.ado-tech.com/Camera-setting/Android/${model}.json`, { method: "GET" });
            if (!response.ok) {
                resolve({
                    success: false,
                    data: null,
                })
            }
            const data = await response.json();
            resolve({
                success: true,
                data,
            });
        } catch (error) {
            resolve({
                success: false,
                data: null,
            })
        }
    })
};
// Función mejorada para obtener la mejor cámara trasera
export const getBestBackCamera = async (model) => {
    const videoInputs = await getVideoInputs()

    // Buscar cámara trasera por etiquetas comunes
    const backCameraKeywords = [
        'Facing back:/dev/video10',
        // Inglés
        "back", "rear", "environment", "main", "primary",
        "world", "outer", "external", "outward", "facing-back",

        // Español
        "trasera", "posterior", "principal", "externa",
        "exterior", "ambiente", "mundo", "primaria",

        // Francés
        "arrière", "principale", "environnement", "extérieure",
        "externe", "primaire", "dorsale", "verso",

        // Portugués
        "traseira", "principal", "ambiente", "externa",
        "exterior", "primária", "mundo", "dorsal",

        // Italiano
        "posteriore", "principale", "ambiente", "esterna",
        "esterno", "primaria", "retro", "dorsale",

        // Alemán
        "hinten", "rück", "haupt", "umgebung",
        "außen", "primär", "extern", "rückseitig", "äußere",

        // Chino simplificado
        "后置", "主摄像头", "后摄", "主摄",
        "后置摄像头", "环境", "外置", "背面",

        // Chino tradicional
        "後置", "主攝像頭", "後攝", "背面",

        // Coreano
        "후면", "메인", "후방", "외부",
        "주", "환경", "뒷면", "외측",

        // Japonés
        "背面", "メイン", "後方", "外側",
        "リア", "アウト", "主", "環境",

        // Neerlandés
        "achter", "achterkant", "hoofd", "primair",
        "extern", "omgeving",

        // Ruso
        "задняя", "основная", "тыльная", "внешняя",
        "главная", "среда",

        // Árabe
        "خلفي", "خلفية", "رئيسي", "رئيسية",
        "بيئة", "خارجي",

        // Hindi
        "पीछे", "मुख्य", "पिछला", "बाहरी",

        // Términos técnicos comunes
        "cam0", "camera0", "cam-0", "camera-0",
        "cam_0", "camera_0", "ultra-wide-back",
        "wide-back", "telephoto-back", "env",

        // Variaciones con guiones y espacios
        "back-facing", "back_facing", "rear-facing", "rear_facing",
        "outward-facing", "outward_facing", "world-facing", "world_facing"
    ];

    let bestCamera = videoInputs.find((device) =>
        backCameraKeywords.some((keyword) => device.label.toLowerCase().includes(keyword)),
    )
    // Validación adicional para dispositivos Android (Samsung)
    const deviceInfo = initDevice()
    if (deviceInfo.isAndroid) {
        if (model) {
            const config = await fetchConfig(model);
            if (config.success) {
                const bestCamerasOfModel = config.data.cameras.rear.map(camera => camera.label)
                const bestCameraAndroid = videoInputs.find((device) =>
                    bestCamerasOfModel.some((keyword) => device.label.toLowerCase().includes(keyword)),
                )
                if (bestCameraAndroid) {
                    return bestCameraAndroid;
                }
            }
        }

        const keywordsPrimary = [
            'camera 0, facing back',
            'Facing back:0',
            'Facing back: 0',
            'Facing back:/dev/video10',
        ]
        const bestFirstCameraAndroid = videoInputs.find((device) =>
            keywordsPrimary.some((keyword) => device.label.toLowerCase().includes(keyword)),
        )
        if (bestFirstCameraAndroid) {
            return bestFirstCameraAndroid;
        }
        const keywordsSamsung = [
            'camera 2, facing back',
            'Camera 2, facing back',
            'camera2 2, facing back',
            'Camera2 2, facing back',
            'Facing back:2',
            'Facing back: 2',
            'facing back:2',
            'facing back: 2',
            'Facing back:/dev/video10',
        ];
        const bestCameraAndroid = videoInputs.find((device) =>
            keywordsSamsung.some((keyword) => device.label.toLowerCase().includes(keyword)),
        )
        if (bestCameraAndroid) {
            return bestCameraAndroid;
        }
    }

    // Si no encuentra por etiqueta, usar la última cámara (generalmente es la trasera)
    if (!bestCamera && videoInputs.length > 1) {
        bestCamera = videoInputs[videoInputs.length - 1]
    }

    // Fallback a la primera cámara disponible
    if (!bestCamera && videoInputs.length > 0) {
        bestCamera = videoInputs[0]
    }

    return bestCamera
}

// Función para verificar capacidades de la cámara
export const getCameraCapabilities = async (deviceId) => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: deviceId } },
        })

        const track = stream.getVideoTracks()[0]
        const capabilities = track.getCapabilities()
        const settings = track.getSettings()

        // Limpiar el stream temporal
        track.stop()

        return { capabilities, settings }
    } catch (error) {
        console.error("Error getting camera capabilities:", error)
        return { capabilities: {}, settings: {} }
    }
}

// Configuraciones optimizadas para diferentes dispositivos
export const getOptimizedConstraints = async (deviceInfo, cameraType = "environment") => {
    const { isIos, isAndroid, isSafari, isWebView } = deviceInfo

    const constraints = {
        audio: false,
        video: {
            facingMode: cameraType,
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            aspectRatio: { ideal: 16/9 }
        },
    }

    // Configuración específica para iOS
    if (isIos) {
        const bestCamera = await getBestBackCamera()

        if (bestCamera?.deviceId) {
            const { capabilities } = await getCameraCapabilities(bestCamera.deviceId)

            constraints.video = {
                deviceId: { exact: bestCamera.deviceId },
                width: { ideal: 1920, max: 1920 },
                height: { ideal: 1080, max: 1080 },
                frameRate: { ideal: 30, max: 30 },
            }

            // Configurar enfoque si está disponible
            if (capabilities.focusMode) {
                constraints.video.focusMode = capabilities.focusMode.includes("continuous")
                    ? "continuous"
                    : capabilities.focusMode.includes("single-shot")
                        ? "single-shot"
                        : "manual"
            }

            // Configurar zoom si está disponible
            if (capabilities.zoom) {
                constraints.video.zoom = 1.0
            }

            // Configurar exposición automática
            if (capabilities.exposureMode) {
                constraints.video.exposureMode = capabilities.exposureMode.includes("continuous") ? "continuous" : "manual"
            }

            // Configurar balance de blancos
            if (capabilities.whiteBalanceMode) {
                constraints.video.whiteBalanceMode = capabilities.whiteBalanceMode.includes("continuous")
                    ? "continuous"
                    : "manual"
            }
        }
    }
    // Configuración para Android
    else if (isAndroid) {
        const bestCamera = await getBestBackCamera()
        const { capabilities } = await getCameraCapabilities(bestCamera.deviceId)
        onReportEvent(SDK_EVENT_KEYS.CameraQualityDetected, {data: JSON.stringify(capabilities)}, 'CardCapture').catch()


        if (bestCamera?.deviceId) {
            constraints.video = {
                deviceId: { exact: bestCamera.deviceId },
                width: { ideal: 1920, max: 1920 },
                height: { ideal: 1080, max: 1080 },
                focusMode: "continuous",
                frameRate: { ideal: 30 },
                aspectRatio: { ideal: 16/9 }
            }
        }
    }
    // Configuración para escritorio/Mac
    else {
        constraints.video = {
            facingMode: cameraType,
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30 },
        }
    }

    return constraints
}

// Función para aplicar configuraciones de enfoque después de iniciar la cámara
export const applyFocusSettings = async (stream) => {
    try {
        const track = stream.getVideoTracks()[0]
        const capabilities = track.getCapabilities()
        const settings = track.getSettings()

        // Configuraciones avanzadas para iOS
        if (initDevice().isIos && capabilities.focusMode) {
            const constraints = {}

            // Configurar enfoque continuo si está disponible
            if (capabilities.focusMode.includes("continuous")) {
                constraints.focusMode = "continuous"
            }

            // Configurar distancia de enfoque si está disponible
            if (capabilities.focusDistance) {
                constraints.focusDistance = capabilities.focusDistance.max * 0.1 // Enfoque a distancia media
            }

            // Aplicar configuraciones
            if (Object.keys(constraints).length > 0) {
                await track.applyConstraints({ advanced: [constraints] })
            }
        }

        return true
    } catch (error) {
        console.error("Error applying focus settings:", error)
        return false
    }
}
